import {
  normalizeServiceRecommendation,
  recommendServiceFromFitScore,
  type ServiceRecommendationV1,
} from './service-recommendation.js'
import type {
  ConsultantStateSnapshot,
  ConversationCategory,
  ConversationFlowState,
  ConversationObjective,
} from '../../types/conversation-flow-types.js'

export type CanonicalConversationFlowV1 = ConversationFlowState

export interface CanonicalAgentStateV1 {
  leadScore: number | null
  fitScore: {
    workshop: number | null
    consulting: number | null
  }
  budget: {
    hasExplicit: boolean
    minUsd: number | null
    maxUsd: number | null
    urgency: number | null
  }
  timeline: {
    urgency: number | null
    explicit: string | null
  }
  interestLevel: number | null
  currentObjection: 'price' | 'timing' | 'authority' | 'need' | 'trust' | null
  researchConfidence: number | null
  roleConfidence: number | null
  personSeniority: string | null
  companyEmployeeCount: number | null
  pitchDelivered: boolean
  pitchTriggeredByUser: boolean
  calendarBooked: boolean
  /** Derived from fitScore with the one 0.7 rule. Proposal, admin and PDF read it. */
  recommendation?: ServiceRecommendationV1 | null
}

export type CanonicalAgentStatePatchV1 = Partial<
  Omit<CanonicalAgentStateV1, 'fitScore' | 'budget' | 'timeline' | 'recommendation'>
> & {
  fitScore?: Partial<CanonicalAgentStateV1['fitScore']>
  budget?: Partial<CanonicalAgentStateV1['budget']>
  timeline?: Partial<CanonicalAgentStateV1['timeline']>
}

export interface CanonicalAgentSummaryV1 {
  executiveSummary: string | null
  industryIntelligence: {
    industry: string | null
    marketContext: string | null
    competitiveLandscape: string | null
  }
  multimodalInteractionSummary: {
    voice: string | null
    screenShare: string | null
    webcam: string | null
    documentsReviewed: string[]
    engagementScore: string | null
  }
  keyFindings: {
    goals: string | null
    painPoints: string[]
    currentSituation: string | null
    dataReality: string | null
    teamReadiness: string | null
    budgetSignals: string | null
  }
  multimodalEvidence: {
    visualDiagnosis: string | null
    voiceInsights: string | null
    documentAnalysis: string | null
  }
  callAgenda: {
    focus: string | null
    questions: string[]
    desiredOutcome: string | null
  }
  recommendedSolution: string | null
  solutionRationale: string | null
  expectedROI: string | null
  pricingBallpark: string | null
  nextSteps: string | null
  roiData: {
    investment: number | null
    projectedSavings: number | null
    roiPercentage: number | null
    paybackPeriod: string | null
  } | null
}

const CONVERSATION_CATEGORIES: readonly ConversationCategory[] = [
  'goals',
  'pain',
  'data',
  'readiness',
  'budget',
  'success',
]

const CONVERSATION_OBJECTIVES: readonly ConversationObjective[] = [
  'clarity',
  'diagnose',
  'prove-value',
  'recommend',
  'validate',
  'close',
]

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
    : []
}

function categoryValue(value: unknown): ConversationCategory | null {
  return typeof value === 'string' &&
    CONVERSATION_CATEGORIES.includes(value as ConversationCategory)
    ? (value as ConversationCategory)
    : null
}

function normalizeConsultantState(value: unknown): ConsultantStateSnapshot | undefined {
  if (!isRecord(value)) return undefined
  const identityConfidence = value.identityConfidence
  const segment = value.segment
  const buyingIntent = value.buyingIntent
  const urgency = value.urgency
  const serviceFit = value.serviceFit
  const multimodalOpportunity = value.multimodalOpportunity
  const bookingReadiness = value.bookingReadiness
  if (
    !['none', 'provisional', 'verified'].includes(String(identityConfidence)) ||
    ![
      'local_service_company',
      'small_business_owner',
      'startup_founder',
      'marketing_manager',
      'cto_product_lead',
      'enterprise_buyer',
      'intern_student',
      'unknown',
    ].includes(String(segment)) ||
    !['none', 'low', 'medium', 'high'].includes(String(buyingIntent)) ||
    !['low', 'medium', 'high'].includes(String(urgency)) ||
    ![
      'basic_ai_workshop',
      'team_workshop',
      'consulting_advisory',
      'implementation_sprint',
      'enterprise_transformation',
      'no_sale_education',
      'diagnostic',
    ].includes(String(serviceFit)) ||
    ![null, 'screen_share', 'upload', 'url', 'webcam'].includes(
      multimodalOpportunity as null | string,
    ) ||
    !['not_ready', 'emerging', 'ready'].includes(String(bookingReadiness))
  ) {
    return undefined
  }
  return {
    identityConfidence: identityConfidence as ConsultantStateSnapshot['identityConfidence'],
    segment: segment as ConsultantStateSnapshot['segment'],
    corePainCaptured: value.corePainCaptured === true,
    buyingIntent: buyingIntent as ConsultantStateSnapshot['buyingIntent'],
    urgency: urgency as ConsultantStateSnapshot['urgency'],
    serviceFit: serviceFit as ConsultantStateSnapshot['serviceFit'],
    evidenceAvailable: value.evidenceAvailable === true,
    multimodalOpportunity:
      multimodalOpportunity as ConsultantStateSnapshot['multimodalOpportunity'],
    bookingReadiness: bookingReadiness as ConsultantStateSnapshot['bookingReadiness'],
    shouldOfferServiceFit: value.shouldOfferServiceFit === true,
    signals: stringArray(value.signals),
  }
}

export function createEmptyCanonicalConversationFlow(
  totalUserTurns = 0,
): CanonicalConversationFlowV1 {
  return {
    covered: {
      goals: false,
      pain: false,
      data: false,
      readiness: false,
      budget: false,
      success: false,
    },
    recommendedNext: null,
    evidence: {},
    insights: {},
    coverageOrder: [],
    totalUserTurns,
    firstUserTimestamp: null,
    latestUserTimestamp: null,
    shouldOfferRecap: false,
  }
}

export function normalizeCanonicalConversationFlow(
  value: unknown,
  fallbackUserTurns = 0,
): CanonicalConversationFlowV1 | null {
  if (!isRecord(value)) return null
  const coveredValue = isRecord(value.covered) ? value.covered : {}
  const evidenceValue = isRecord(value.evidence) ? value.evidence : {}
  const insightsValue = isRecord(value.insights) ? value.insights : {}
  const flow = createEmptyCanonicalConversationFlow(fallbackUserTurns)

  for (const category of CONVERSATION_CATEGORIES) {
    flow.covered[category] = coveredValue[category] === true
    const evidence = stringArray(evidenceValue[category])
    if (evidence.length > 0) flow.evidence[category] = evidence
    const insight = insightsValue[category]
    if (isRecord(insight)) {
      const firstTurnIndex = finiteNumber(insight.firstTurnIndex)
      const firstMessageId = stringValue(insight.firstMessageId)
      const firstTimestamp = finiteNumber(insight.firstTimestamp)
      if (firstTurnIndex !== null && firstMessageId) {
        flow.insights[category] = {
          firstTurnIndex,
          firstMessageId,
          firstTimestamp,
        }
      }
    }
  }

  flow.recommendedNext = categoryValue(value.recommendedNext)
  const totalUserTurns = finiteNumber(value.totalUserTurns)
  flow.totalUserTurns = Math.max(0, Math.trunc(totalUserTurns ?? fallbackUserTurns))
  flow.firstUserTimestamp = finiteNumber(value.firstUserTimestamp)
  flow.latestUserTimestamp = finiteNumber(value.latestUserTimestamp)
  flow.shouldOfferRecap = value.shouldOfferRecap === true
  flow.coverageOrder = Array.isArray(value.coverageOrder)
    ? value.coverageOrder.flatMap(item => {
        if (!isRecord(item)) return []
        const category = categoryValue(item.category)
        const firstTurnIndex = finiteNumber(item.firstTurnIndex)
        const firstMessageId = stringValue(item.firstMessageId)
        if (!category || firstTurnIndex === null || !firstMessageId) return []
        return [{
          category,
          firstTurnIndex,
          firstMessageId,
          firstTimestamp: finiteNumber(item.firstTimestamp),
        }]
      })
    : []

  const lastResearchTurn = finiteNumber(value.lastResearchTurn)
  if (lastResearchTurn !== null) flow.lastResearchTurn = Math.max(0, Math.trunc(lastResearchTurn))
  if (
    typeof value.conversationObjective === 'string' &&
    CONVERSATION_OBJECTIVES.includes(value.conversationObjective as ConversationObjective)
  ) {
    flow.conversationObjective = value.conversationObjective as ConversationObjective
  }
  const consultantState = normalizeConsultantState(value.consultantState)
  if (consultantState) flow.consultantState = consultantState
  const rejectedTopics = stringArray(value.rejectedTopics)
  if (rejectedTopics.length > 0) flow.rejectedTopics = rejectedTopics
  return flow
}

export function createInitialCanonicalAgentState(): CanonicalAgentStateV1 {
  return {
    leadScore: null,
    fitScore: { workshop: null, consulting: null },
    budget: { hasExplicit: false, minUsd: null, maxUsd: null, urgency: null },
    timeline: { urgency: null, explicit: null },
    interestLevel: null,
    currentObjection: null,
    researchConfidence: null,
    roleConfidence: null,
    personSeniority: null,
    companyEmployeeCount: null,
    pitchDelivered: false,
    pitchTriggeredByUser: false,
    calendarBooked: false,
    recommendation: null,
  }
}

export function mergeCanonicalAgentState(
  current: CanonicalAgentStateV1,
  patch: CanonicalAgentStatePatchV1,
): CanonicalAgentStateV1 {
  const fitScore = { ...current.fitScore, ...patch.fitScore }
  return {
    ...current,
    ...patch,
    fitScore,
    budget: { ...current.budget, ...patch.budget },
    timeline: { ...current.timeline, ...patch.timeline },
    recommendation: patch.fitScore
      ? recommendServiceFromFitScore(fitScore)
      : (normalizeServiceRecommendation(
          (patch as { recommendation?: unknown }).recommendation ?? current.recommendation,
        ) ?? recommendServiceFromFitScore(fitScore)),
  }
}

function normalizeSummarySection(value: unknown, keys: readonly string[]) {
  const record = isRecord(value) ? value : {}
  return Object.fromEntries(keys.map(key => [key, stringValue(record[key])]))
}

export function normalizeCanonicalAgentSummary(
  value: unknown,
): CanonicalAgentSummaryV1 | null {
  if (!isRecord(value)) return null
  const industry = normalizeSummarySection(value.industryIntelligence, [
    'industry',
    'marketContext',
    'competitiveLandscape',
  ])
  const multimodal = isRecord(value.multimodalInteractionSummary)
    ? value.multimodalInteractionSummary
    : {}
  const findings = isRecord(value.keyFindings) ? value.keyFindings : {}
  const evidence = normalizeSummarySection(value.multimodalEvidence, [
    'visualDiagnosis',
    'voiceInsights',
    'documentAnalysis',
  ])
  const agendaValue = isRecord(value.callAgenda) ? value.callAgenda : {}
  const roiValue = isRecord(value.roiData) ? value.roiData : null
  return {
    executiveSummary: stringValue(value.executiveSummary),
    industryIntelligence: {
      industry: industry.industry ?? null,
      marketContext: industry.marketContext ?? null,
      competitiveLandscape: industry.competitiveLandscape ?? null,
    },
    multimodalInteractionSummary: {
      voice: stringValue(multimodal.voice),
      screenShare: stringValue(multimodal.screenShare),
      webcam: stringValue(multimodal.webcam),
      documentsReviewed: stringArray(multimodal.documentsReviewed),
      engagementScore: stringValue(multimodal.engagementScore),
    },
    keyFindings: {
      goals: stringValue(findings.goals),
      painPoints: stringArray(findings.painPoints),
      currentSituation: stringValue(findings.currentSituation),
      dataReality: stringValue(findings.dataReality),
      teamReadiness: stringValue(findings.teamReadiness),
      budgetSignals: stringValue(findings.budgetSignals),
    },
    multimodalEvidence: {
      visualDiagnosis: evidence.visualDiagnosis ?? null,
      voiceInsights: evidence.voiceInsights ?? null,
      documentAnalysis: evidence.documentAnalysis ?? null,
    },
    callAgenda: {
      focus: stringValue(agendaValue.focus),
      questions: stringArray(agendaValue.questions),
      desiredOutcome: stringValue(agendaValue.desiredOutcome),
    },
    recommendedSolution: stringValue(value.recommendedSolution),
    solutionRationale: stringValue(value.solutionRationale),
    expectedROI: stringValue(value.expectedROI),
    pricingBallpark: stringValue(value.pricingBallpark),
    nextSteps: stringValue(value.nextSteps),
    roiData: roiValue
      ? {
          investment: finiteNumber(roiValue.investment),
          projectedSavings: finiteNumber(roiValue.projectedSavings),
          roiPercentage: finiteNumber(roiValue.roiPercentage),
          paybackPeriod: stringValue(roiValue.paybackPeriod),
        }
      : null,
  }
}
